import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, ArrowRight, CheckCircle2, ClipboardCheck, Database, Download, Pencil, Send } from 'lucide-react';
import { Button } from '../../components/Button';
import { PageHeader } from '../../components/common/PageHeader';
import { Panel } from '../../components/common/Panel';
import { StatusBadge } from '../../components/common/StatusBadge';
import { usePrototype } from '../../contexts/PrototypeContext';
import { getDriver } from '../../data/drivers';
import { verifyStatus } from '../../utils/status';
import type { Cng, ValidationField } from '../../types/domain';
import { toBn } from '../../utils/bn';
function buildFields(c: Cng): ValidationField[] {
  const d = getDriver(c.driverIds[0]);
  const base: ValidationField[] = [{
    label: 'রেজিস্ট্রেশন (Registration)',
    brta: c.reg,
    dmp: c.reg
  }, {
    label: 'মালিক (Owner)',
    brta: c.owner.name,
    dmp: c.owner.name
  }, {
    label: 'চেসিস (Chassis)',
    brta: c.chassis,
    dmp: c.chassis
  }, {
    label: 'ইঞ্জিন (Engine)',
    brta: c.engine,
    dmp: c.engine
  }, {
    label: 'ফিটনেস (Fitness)',
    brta: c.fitnessExpiry,
    dmp: c.fitnessExpiry
  }, {
    label: 'ড্রাইভিং লাইসেন্স (Driver License)',
    brta: d ? `${d.license} (সক্রিয়)` : '—',
    dmp: d ? `${d.license} (সক্রিয়)` : '—'
  }];
  const map: Record<string, string> = {
    'মালিকের নাম': 'মালিক (Owner)',
    'ইঞ্জিন নং': 'ইঞ্জিন (Engine)',
    'ফিটনেস মেয়াদ': 'ফিটনেস (Fitness)',
    'ড্রাইভিং লাইসেন্স': 'ড্রাইভিং লাইসেন্স (Driver License)'
  };
  (c.mismatches ?? []).forEach(m => {
    const f = base.find(b => b.label === map[m.label]);
    if (f) {
      f.brta = m.brta;
      f.dmp = m.dmp;
    }
  });
  return base;
}
export function DataValidation() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    visibleCngs,
    getCng,
    setStage
  } = usePrototype();
  const queue = useMemo(() => visibleCngs.filter(c => c.stage >= 1 && c.stage <= 3), [visibleCngs]);
  const selectedId = id ?? queue[0]?.id ?? 'c2';
  const cng = getCng(selectedId);
  const [resolved, setResolved] = useState<Record<string, boolean>>({});
  useEffect(() => setResolved({}), [selectedId]);
  if (!cng) return <p data-id="element-1413" data-mp="23768ghpeqqeo">রেকর্ড পাওয়া যায়নি।</p>;
  const fields = buildFields(cng);
  const isMatch = (f: ValidationField) => f.brta === f.dmp || resolved[f.label];
  const mismatchCount = fields.filter(f => !isMatch(f)).length;
  const alreadyValidated = cng.stage >= 2;
  return <div data-id="element-1414" data-mp="1y7bneusbcv1j">
      <PageHeader title="তথ্য যাচাই" subtitle="BRTA উৎস তথ্য ও ডিএমপি সিস্টেমের তথ্য পাশাপাশি তুলনা করে যাচাই করুন" actions={<Link to="/admin/brta-import" className="text-sm font-semibold text-navy-700" data-id="element-1416" data-mp="1ofe6d7emj8uw">BRTA আমদানি →</Link>} data-id="element-1415" data-mp="1y7tpi81nq3hd" />
      <div className="grid gap-4 xl:grid-cols-[260px_1fr]" data-id="element-1417" data-mp="1afwgspti7rkh">
        <Panel title={`যাচাই সারি (${toBn(queue.length)})`} bodyClassName="p-2" data-id="element-1418" data-mp="1ff9btxuj44y5">
          <ul className="space-y-1" data-id="element-1419" data-mp="1fhbyc7wh3fy4">
            {queue.map(c => {
            const v = verifyStatus(c.stage, c);
            return <li key={c.id} data-id="element-1420" data-mp="qv5xg0u05b1k">
                  <button onClick={() => navigate(`/admin/validation/${c.id}`)} className={`w-full rounded-md px-3 py-2.5 text-left ${c.id === selectedId ? 'bg-navy-50 ring-1 ring-navy-200' : 'hover:bg-slate-50'}`} data-id="element-1421" data-mp="qvxk2m5mfyfp">
                    <p className="text-sm font-semibold text-navy-900" data-id="element-1422" data-mp="1er9zojxaod2x">{c.reg}</p>
                    <div className="mt-1 flex items-center justify-between gap-2" data-id="element-1423" data-mp="19tqnrqo2kce5">
                      <span className="truncate text-[11px] text-slate-500" data-id="element-1424" data-mp="1ohv6uvr1apxg">{c.owner.name}</span>
                      <StatusBadge tone={v.tone} className="!px-1.5 !text-[10px]" data-id="element-1425" data-mp="1jj1ux7reb6ze">{v.label}</StatusBadge>
                    </div>
                  </button>
                </li>;
          })}
            {queue.length === 0 && <li className="p-3 text-xs text-slate-500" data-id="element-1426" data-mp="1skhsug10xgmq">সারিতে কোনো রেকর্ড নেই</li>}
          </ul>
        </Panel>

        <div className="space-y-4" data-id="element-1427" data-mp="3mz5dhsob2otq">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-white px-5 py-4 shadow-sm" data-id="element-1428" data-mp="3mxu7rnbav6zz">
            <div data-id="element-1429" data-mp="k3uoc2ilp49">
              <Link to={`/admin/cng/${cng.id}`} className="font-heading text-xl font-bold text-navy-900 hover:underline" data-id="element-1430" data-mp="lnbq4vzw473">{cng.reg}</Link>
              <p className="text-xs text-slate-500" data-id="element-1431" data-mp="4o1xoxuynk2l">উৎস: {cng.source} {cng.importBatch && `• ${cng.importBatch}`} • {cng.owner.name}</p>
            </div>
            {mismatchCount > 0 ? <span className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800" data-id="element-1432" data-mp="390fe26e0kd27"><AlertTriangle className="h-4 w-4" data-id="element-1433" data-mp="3d43oqxxxzoro" />{toBn(mismatchCount)}টি তথ্য অমিল</span> : <span className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700" data-id="element-1434" data-mp="sbjreyxscs87"><CheckCircle2 className="h-4 w-4" data-id="element-1435" data-mp="xacuryjcz2un" />সকল তথ্য মিলেছে</span>}
          </div>

          <Panel bodyClassName="p-0" data-id="element-1436" data-mp="17e9yxsxs6rsv">
            <div className="grid grid-cols-[180px_1fr_1fr_120px] border-b border-border bg-slate-50 text-xs font-semibold text-slate-600" data-id="element-1437" data-mp="17d8gv4icl51n">
              <div className="px-4 py-3" data-id="element-1438" data-mp="xoqi0stmp80n">ফিল্ড</div>
              <div className="flex items-center gap-2 border-l border-border px-4 py-3" data-id="element-1439" data-mp="xnfe8t06o09j"><Download className="h-3.5 w-3.5" data-id="element-1440" data-mp="tnohm8nz6px3" />BRTA Source Data</div>
              <div className="flex items-center gap-2 border-l border-border px-4 py-3" data-id="element-1441" data-mp="oo237vwev3mp"><Database className="h-3.5 w-3.5" data-id="element-1442" data-mp="opd85z1lehfe" />DMP Data</div>
              <div className="border-l border-border px-4 py-3" data-id="element-1443" data-mp="jope14i2hkgy">ফলাফল</div>
            </div>
            {fields.map(f => {
            const m = isMatch(f);
            const fixed = resolved[f.label];
            return <div key={f.label} className={`grid grid-cols-[180px_1fr_1fr_120px] border-b border-border text-sm last:border-0 ${m ? '' : 'bg-amber-50/60'}`} data-id="element-1444" data-mp="aq3ogpe6h56o">
                  <div className="px-4 py-3.5 text-xs font-medium text-slate-600" data-id="element-1445" data-mp="2521xaq8krz21">{f.label}</div>
                  <div className={`border-l border-border px-4 py-3.5 font-medium ${m ? 'text-emerald-800' : 'text-red-700'}`} data-id="element-1446" data-mp="2fegkjh7k3cz">{f.brta}</div>
                  <div className={`border-l border-border px-4 py-3.5 font-medium ${m ? 'text-emerald-800' : 'text-amber-800'}`} data-id="element-1447" data-mp="77o7o2u279gu">
                    {fixed ? f.brta : f.dmp}
                    {!m && <button onClick={() => setResolved(r => ({
                  ...r,
                  [f.label]: true
                }))} className="ml-2 rounded border border-navy-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-navy-700 hover:bg-navy-50" data-id="element-1448" data-mp="3tjv5nqsxcmcd">
                        BRTA তথ্য গ্রহণ
                      </button>}
                  </div>
                  <div className="flex items-center border-l border-border px-4" data-id="element-1449" data-mp="h417zjk40lkk">
                    {m ? <StatusBadge tone="green" data-id="element-1450" data-mp="2wvyke8mjl2ut">{fixed ? 'সংশোধিত' : 'মিলেছে'}</StatusBadge> : <StatusBadge tone="orange" data-id="element-1451" data-mp="2wxru5wsgc371">অমিল</StatusBadge>}
                  </div>
                </div>;
          })}
          </Panel>

          <div className="flex flex-wrap items-center justify-end gap-2 rounded-lg border border-border bg-white p-4" data-id="element-1452" data-mp="2tcpzytyrgxr2">
            <Button variant="outline" disabled={mismatchCount === 0} onClick={() => {
            const all: Record<string, boolean> = {};
            fields.forEach(f => all[f.label] = true);
            setResolved(all);
            toast.success('BRTA উৎসের তথ্য গ্রহণ করা হয়েছে');
          }} data-id="element-1453" data-mp="2pbmik312etnq">
              <Download className="h-4 w-4" data-id="element-1454" data-mp="25p81ygowl3fn" />তথ্য গ্রহণ করুন
            </Button>
            <Button variant="outline" onClick={() => navigate(`/admin/cng/${cng.id}/edit`)} data-id="element-1455" data-mp="2kdl3ruao9gi2"><Pencil className="h-4 w-4" data-id="element-1456" data-mp="2fmct6uxlvp3b" />সংশোধন করুন</Button>
            <Button variant="outline" disabled={mismatchCount > 0 || alreadyValidated} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-700" onClick={() => {
            setStage(cng.id, 2);
            toast.success(`${cng.reg} যাচাইকৃত হিসেবে চিহ্নিত`);
          }} data-id="element-1457" data-mp="2fl1omswc6db5">
              <ClipboardCheck className="h-4 w-4" data-id="element-1458" data-mp="16dz3yeqdomul" />{alreadyValidated ? 'যাচাইকৃত' : 'যাচাইকৃত হিসেবে চিহ্নিত করুন'}
            </Button>
            <Button disabled={mismatchCount > 0} onClick={() => {
            setStage(cng.id, Math.max(cng.stage, 3));
            toast.success('অনুমোদনকারী কর্মকর্তার কাছে পাঠানো হয়েছে');
            navigate(`/admin/approval/${cng.id}`);
          }} data-id="element-1459" data-mp="1bewjejk072se">
              <Send className="h-4 w-4" data-id="element-1460" data-mp="1u3bat7y8gitq" />অনুমোদনের জন্য পাঠান <ArrowRight className="h-4 w-4" data-id="element-1461" data-mp="1u3jov363du6i" />
            </Button>
          </div>
        </div>
      </div>
    </div>;
}